/* BRIEF 136 §3(3) [RULED 2026-08-25, Laurent] — WHAT SITS AT AN EVENT ROW'S
 * RIGHT EDGE: the day counter, or the status glyph that replaces it.
 *
 *   Confirmed    shows nothing extra: just the counter, as today
 *   Unconfirmed  a circle-question icon INSTEAD of the counter
 *   Cancelled    a circle-off icon instead
 *   — both "in the counter's colour"
 *
 * ── WHY A COMPONENT AND NOT TWO EDITS ───────────────────────────────────────
 *
 * The counter is drawn TWICE, and the brief's own proof spans both: §3 cites
 * `CaseEventRow.tsx:201` and its fence says "nothing in the AGENDA may change
 * appearance", which is `AgendaView.tsx:577`. 102 §2f already found these two
 * had written the same three branches separately and moved the ARITHMETIC into
 * `src/lib/dayCountdown.ts`; the MARKUP stayed in both places. Adding the icon
 * to one and not the other would give an unconfirmed event a glyph in the case
 * view and a countdown in the agenda — 115's defect exactly, two renderers for
 * one thing with every later fix landing in one of them.
 *
 * ── THE COLOUR IS THE HOST'S, AND IS NOT RESTATED HERE ──────────────────────
 *
 * §3: "taking the counter's own colour — read that colour where the counter
 * computes it rather than restating it." The two hosts do NOT agree on that
 * colour and this component must not reconcile them:
 *
 *   CaseEventRow.tsx:205   `evDayClr(diff, ev.type)`  — type-aware
 *   AgendaView.tsx:742     an inline ladder: 1 -> --rd, 2-3 -> --am,
 *                          4-7 -> --gn, else --tm
 *
 * Each passes what it already computed. Reconciling them is a real question
 * and a separate one; this section is not the place to answer it silently.
 *
 * ── WHEN IT DRAWS ───────────────────────────────────────────────────────────
 *
 * The counter half is unchanged: absent when the row has no date, which is
 * 102 §4a's "the row does not jump when the label is absent" — the auto margin
 * belongs to this element, so with nothing to draw there is no margin.
 *
 * The icon half draws whenever the status declares one, INCLUDING on a dated
 * row's dateless sibling. An event somebody marked unconfirmed says so whether
 * or not it has a date; and no untouched event can reach that branch, because
 * `confirmed` declares no icon (src/lib/eventStatus.ts).
 */
import { eventStatusIcon } from "../lib/eventStatus";
import { getEventStatusMeta } from "../lib/taxonomyHelpers";
import { iconByName } from "../lib/lucideRegistry";
import { I } from "../utils";
import { FS } from "../lib/tokens";

export default function EventDayMark({
  label,
  color,
  isPast,
  status,
  statusEntries,
}: {
  /** `dayCountdownLabel`'s answer — `null` for a row with no date. */
  label: string | null;
  /** The host's own counter colour, already computed. */
  color: string;
  /** The host's own "this date has passed" answer (it italicises). */
  isPast: boolean;
  /** The row's DISPLAY status, from `eventStatusOf`. */
  status: string;
  /** `useTaxonomy("event_status").entries`, so a firm's own glyph wins. */
  statusEntries?: unknown;
}) {
  const iconName = eventStatusIcon(status, statusEntries);
  if (iconName) {
    const Ic = iconByName(iconName);
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          color,
          flexShrink: 0,
          marginLeft: "auto",
        }}
        /* The word, for anyone who does not read the glyph — and it is the
           FIRM'S word, because these four are renameable like every taxonomy
           entry. */
        title={getEventStatusMeta(status, statusEntries).label}
      >
        <Ic size={I.m} />
      </span>
    );
  }
  if (label === null) return null;
  return (
    <span
      style={{
        fontWeight: 700,
        fontSize: FS.s13,
        color,
        fontStyle: isPast ? "italic" : "normal",
        flexShrink: 0,
        marginLeft: "auto",
      }}
    >
      {label}
    </span>
  );
}
